import { formatDate } from '../../filters'
import { getFilterOptions } from './helper'
import { bus } from '../../utils'
import DeviceInfo from '../DeviceInfo/DeviceInfo.vue'
import DeviceInfoBoxes from '../DeviceInfoBoxes/DeviceInfoBoxes.vue'
import DeviceLock from '../DeviceLock/DeviceLock.vue'

export default {
  beforeDestroy () {
    bus.$off('update-locked-device-list')
  },
  components: { DeviceInfo, DeviceInfoBoxes, DeviceLock },
  computed: {
    componentTitle () {
      return this.$t(`ComponentTitle.${this.$route.name}`)
    },
    showPagination () {
      return !!this.deviceList.length
    },
    searchBarPlaceholder () {
      if (this.selectedFilter === 'sn') {
        return this.$t('deviceInputSearchBarSNPlaceholder')
      } else if (this.selectedFilter === 'model') {
        return this.$t('deviceInputSearchBarModelPlaceholder')
      } else if (this.selectedFilter === 'online') {
        return this.$t('deviceInputSearchBarOnlinePlaceholder')
      } else if (this.selectedFilter === 'offline') {
        return this.$t('deviceInputSearchBarOfflinePlaceholder')
      } else {
        let selected = this.filterOptions.find(o => o.value === this.selectedFilter)
        return selected ? selected.label : ''
      }
    },
    isSearchBarDisabled () {
      return this.searchBarDisabled
    }
  },
  methods: {
    confirmLock (id, sn) {
      bus.$emit('show-lock-device', {
        show: true,
        info: { id, sn }
      })
    },
    convertSecTime (s) {
      const ms = s * 1000
      return formatDate(ms)
    },
    convertUsTime (us) {
      const ms = us / 1000
      return formatDate(ms)
    },
    deviceSearch (query) {
      this.deviceList = []
      this.expanded = []
      this.pagination.offset = 0
      this.pagination.currentPage = 1

      return this.getDevices(query)
    },
    onSelectionChanged (selectedValue) {
      let query = this.searchQuery

      if (this.selectedFilter === 'online' || this.selectedFilter === 'offline') {
        query = this.selectedFilter === 'online' ? '1' : '0'
        // Trigger search query
        this.deviceSearch(query)

        this.searchQuery = ''
        this.searchBarDisabled = true
      } else {
        // Trigger search query only for the same selection type
        if (query && selectedValue === this.prevSelectedFilter) {
          this.deviceSearch(query)
        }

        this.searchBarDisabled = false
      }

      this.prevSelectedFilter = selectedValue
    },
    getConnectedStatus (connected) {
      return connected ? this.$t('Online') : this.$t('Offline')
    },
    getConnectedStatusIcon (connected) {
      return connected ? 'fa fa-circle device-online-icon' : 'fa fa-circle device-offline-icon'
    },
    async getCurrentPageDevice (pageNumber) {
      this.pagination.offset = this.pagination.limit * (pageNumber - 1)
      await this.getDevices(this.searchQuery + this.prevOrder)
    },
    async getDevices (query = '') {
      this.isLoading = true

      const queryParams = {
        limit: 500,
        offset: this.pagination.offset
      }

      // Example string: type1-1__%ORDER%__state.model__%ORDER%__.desc
      if (typeof query === 'string') {
        let queryExtract = query.split('__%ORDER%__')
        if (queryExtract.length > 1) {
          queryParams.order = queryExtract[1] + queryExtract[2]
          query = queryExtract[0]
        }
      }

      let devices = []
      let mayLoadMore = true

      if (this.selectedFilter) {
        if (this.selectedFilter === 'sn' && query) {
          queryParams.identity = query
        } else if (this.selectedFilter === 'model' && query) {
          queryParams['tag.name'] = 'model'
          queryParams['tag.value'] = query
        } else if (this.selectedFilter === 'online' || this.selectedFilter === 'offline') {
          queryParams['tag.name'] = 'connected'
          this.hideQuery = this.selectedFilter === 'online' ? 1 : 0
          queryParams['tag.value'] = this.hideQuery
        }
      }

      if (mayLoadMore) {
        const res = await this.$api.getDevices(queryParams)
          .then(({ data }) => data)
          .catch(e => {
            console.log(e)
            return { devices: [], mayLoadMore: false }
          })

        devices = [...devices, ...res.devices]
      }
      return this.showList(devices)
    },
    getDeviceModel (model) {
      return model || '--'
    },
    getTotal () {
      let query = {
        fields: JSON.stringify([
          'connected_devices',
          'devices'
        ])
      }
      return this.$api.getTotal(query)
        .then((ret) => { this.total = ret.data })
        .catch(e => console.log(e))
    },
    handleExpand (row, expanded) {
      const arr = []
      expanded.forEach(({ identity }) => {
        const alreadyIn = this.expanded.indexOf(identity) !== -1
        if (!alreadyIn) {
          arr.push(identity)
        }
      })

      this.expanded = arr
      return expanded
    },
    handleSortChange (val) {
      if (val.column) {
        if (val.prop === 'last_seen') {
          val.prop = 'lastseen'
        }
        if (!['identity', 'lastseen'].includes(val.prop)) {
          val.prop = 'state.' + val.prop
        }
        let sortOrder = val.order === 'ascending' ? '.asc' : '.desc'
        this.prevOrder = '__%ORDER%__' + val.prop + '__%ORDER%__' + sortOrder
        let query = this.searchQuery + this.prevOrder
        this.deviceSearch(query)
      }
    },
    getRowKey (row) {
      return row.identity
    },
    showList (devices) {
      this.isLoading = false
      this.deviceList = devices.slice(0, this.pagination.limit)
      // Workaround to hide status value
      if (this.searchQuery !== '') {
        this.hideQuery = this.searchQuery
      }
      this.searchedTotal = this.hideQuery ? devices.length + this.pagination.limit * (this.pagination.currentPage - 1) : this.total.devices
      return devices
    },
    toggleRowExpansion (row) {
      const expandRow = this.expanded.some((id) => row.identity === id)
      const expandedArray = expandRow ? [] : [ row ]

      this.handleExpand(row, expandedArray)
    }
  },
  name: 'DeviceList',
  async created () {
    await this.getTotal()
    await this.getDevices(this.searchQuery)
  },
  data () {
    return {
      deviceList: [],
      expanded: [],
      isLoading: true,
      pagination: {
        currentPage: 1,
        layout: 'prev, pager, next',
        limit: 50,
        offset: 0
      },
      searchedTotal: 0,
      searchQuery: this.$route.params.sn || '',
      total: {},
      filterOptions: [],
      prevSelectedFilter: '',
      selectedFilter: 'sn',
      searchBarDisabled: false,
      prevOrder: '',
      hideQuery: ''
    }
  },
  mounted () {
    this.filterOptions = getFilterOptions()

    bus.$on('update-locked-device-list', (sn) => {
      const device = this.deviceList.find((item) => item.sn === sn)
      device.locked = true
    })
  }
}



// WEBPACK FOOTER //
// ./src/components/Device/Device.js